/**
 * Helpers de date partagés. Volontairement minimaux — Phase 1 ne traite
 * que des dates simples au format ISO `YYYY-MM-DD` (alignement avec les
 * types de `@nestor/shared-types` : FamilyEvent.date, FamilyTask.due,
 * SchoolEntry.date, Expense.date, etc.).
 *
 * En Phase 8 (i18n production), les formats localisés (lundi 5 mai,
 * Mon May 5, Lun May 5) seront générés via `Intl.DateTimeFormat` ou
 * via les helpers de format d'i18next. Ici on reste sur du brut.
 */

/** Date du jour au format `YYYY-MM-DD` (timezone locale du navigateur). */
export function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Vrai si `iso` est strictement après aujourd'hui (date locale).
 * Tolère les formats invalides en retournant `false`.
 */
export function isAfterToday(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  return iso > todayIso();
}

/** Vrai si `iso` correspond exactement à aujourd'hui. */
export function isToday(iso: string): boolean {
  return iso === todayIso();
}

/**
 * Compare deux ISO `YYYY-MM-DD` lexicographiquement (qui équivaut à
 * l'ordre chronologique). Renvoie `<0` si `a < b`, `0` si égalité, `>0`
 * sinon — comparator pour `Array.prototype.sort`.
 */
export function compareIso(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Convertit une `Date` en ISO `YYYY-MM-DD` (timezone locale). */
export function dateToIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Nombre de jours dans un mois (1-12, year complet). */
export function daysInMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate();
}

/** Index 0..6 du jour de semaine du 1er du mois. 0 = dimanche. */
export function firstWeekdayOfMonth(year: number, month1: number): number {
  return new Date(year, month1 - 1, 1).getDay();
}

/**
 * Libellé localisé du mois (ex. « mai 2026 », « May 2026 »).
 * Utilise `Intl.DateTimeFormat` pour rester portable sans dictionnaire
 * en dur. La locale `lang` est typiquement `getLang()`.
 */
export function monthLabel(year: number, month1: number, lang: string): string {
  const d = new Date(year, month1 - 1, 1);
  return d.toLocaleDateString(lang, { month: 'long', year: 'numeric' });
}

/** Construit l'ISO `YYYY-MM-DD` à partir de year/month1/day. */
export function ymdToIso(year: number, month1: number, day: number): string {
  const m = String(month1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

// ── Affichage localisé (audit C) ──────────────────────────────────────
// Source unique des formats de dates visibles : plus jamais d'ISO brut
// (« 2026-07-11 ») ni de `toLocaleDateString()` sans locale (qui suit
// le navigateur, pas la langue choisie dans Nestor).

/** « sam. 11 juil. » — date courte localisée depuis un timestamp ms. */
export function formatMsShort(at: number, lang: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(lang, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(d);
}

/** « sam. 11 juil. » — date courte localisée depuis un ISO `YYYY-MM-DD`. */
export function formatIsoShort(iso: string, lang: string): string {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso; // valeur legacy inattendue : afficher brut
  return formatMsShort(d.getTime(), lang);
}

/** « Juillet 2026 » — nom d'album mensuel localisé (photos, item 10). */
export function monthYearLabel(at: number, lang: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  const label = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(d);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** « sam. 11 juil. · 17:00 » — horodatage ms localisé (date + heure). */
export function formatDateTime(at: number, lang: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  const time = new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }).format(d);
  return `${formatMsShort(at, lang)} · ${time}`;
}
